import { Schema } from "effect";
import { ProjectIdSchema, ThreadIdSchema, UserIdSchema, WorkspaceIdSchema } from "./ids";
import {
  CanonicalIdentifierSchema,
  NonNegativeSafeIntegerSchema,
  PapersFlowBoundaryParseOptions,
  toPapersFlowValidationSchema,
} from "./standard";
import { utf8ByteLength } from "./utf8";

export const PROJECT_TASK_TITLE_MAX_BYTES = 160;
export const PROJECT_TASK_DETAILS_MAX_BYTES = 4 * 1_024;
export const PROJECT_TASK_ACTIVE_LIMIT = 200;
export const PROJECT_TASK_ARCHIVE_PAGE_SIZE = 50;
export const PROJECT_TASK_PROPOSAL_MAX_OPERATIONS = 20;
export const PROJECT_TASK_PROPOSAL_TTL_MS = 7 * 24 * 60 * 60 * 1_000;

const PositiveSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(1),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);

function boundedRequiredText(maximumBytes: number) {
  return Schema.String.check(
    Schema.makeFilter(
      (value) =>
        (value === value.trim() && value.length > 0 && utf8ByteLength(value) <= maximumBytes) ||
        `must be trimmed, non-empty, and at most ${maximumBytes} UTF-8 bytes`,
    ),
  );
}

export const ProjectTaskIdSchema = CanonicalIdentifierSchema.pipe(Schema.brand("ProjectTaskId"));
export const ProjectTaskProposalIdSchema = CanonicalIdentifierSchema.pipe(
  Schema.brand("ProjectTaskProposalId"),
);
export const ProjectTaskProposalOperationIdSchema = CanonicalIdentifierSchema.pipe(
  Schema.brand("ProjectTaskProposalOperationId"),
);
export const ProjectTaskStatusSchema = Schema.Literals(["todo", "in_progress", "done"]);
export const ProjectTaskTitleSchema = boundedRequiredText(PROJECT_TASK_TITLE_MAX_BYTES);
export const ProjectTaskDetailsSchema = boundedRequiredText(PROJECT_TASK_DETAILS_MAX_BYTES);
export const ProjectTaskPositionSchema = NonNegativeSafeIntegerSchema;
export const ProjectTaskRevisionSchema = PositiveSafeIntegerSchema;
export const ProjectTaskTimestampSchema = NonNegativeSafeIntegerSchema;
export const ProjectTaskPublicErrorCodeSchema = Schema.Literals([
  "PROJECT_TASK_INVALID_INPUT",
  "PROJECT_TASK_UNAUTHENTICATED",
  "PROJECT_TASK_FORBIDDEN",
  "PROJECT_TASK_NOT_FOUND",
  "PROJECT_TASK_CONFLICT",
  "PROJECT_TASK_LIMIT_REACHED",
  "PROJECT_TASK_UNAVAILABLE",
]);
export const ProjectTaskPublicErrorSchema = Schema.Struct({
  code: ProjectTaskPublicErrorCodeSchema,
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});
export const ProjectTaskInternalErrorDataSchema = Schema.Struct({
  code: Schema.Literals([
    "UNAUTHENTICATED",
    "FORBIDDEN",
    "INVALID_INPUT",
    "NOT_FOUND",
    "PROJECT_ARCHIVED",
    "TASK_ARCHIVED",
    "TASK_LIMIT_REACHED",
    "REVISION_CONFLICT",
    "PROPOSAL_RESOLVED",
    "PROPOSAL_EXPIRED",
    "IDEMPOTENCY_CONFLICT",
    "INTERNAL_ERROR",
  ]),
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
});

export const ProjectTaskSchema = Schema.Struct({
  taskId: ProjectTaskIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  threadId: Schema.optionalKey(ThreadIdSchema),
  status: ProjectTaskStatusSchema,
  title: ProjectTaskTitleSchema,
  details: ProjectTaskDetailsSchema,
  position: ProjectTaskPositionSchema,
  revision: ProjectTaskRevisionSchema,
  createdBy: UserIdSchema,
  updatedBy: UserIdSchema,
  createdAt: ProjectTaskTimestampSchema,
  updatedAt: ProjectTaskTimestampSchema,
  archivedAt: Schema.optionalKey(ProjectTaskTimestampSchema),
});

export const ProjectTaskCreateOperationSchema = Schema.Struct({
  operationId: ProjectTaskProposalOperationIdSchema,
  kind: Schema.Literal("create"),
  title: ProjectTaskTitleSchema,
  details: ProjectTaskDetailsSchema,
  status: ProjectTaskStatusSchema,
  threadId: Schema.optionalKey(ThreadIdSchema),
});

const ProjectTaskUpdateFieldsSchema = Schema.Struct({
  title: Schema.optionalKey(ProjectTaskTitleSchema),
  details: Schema.optionalKey(ProjectTaskDetailsSchema),
  status: Schema.optionalKey(ProjectTaskStatusSchema),
  threadId: Schema.optionalKey(Schema.NullOr(ThreadIdSchema)),
});

export const ProjectTaskUpdateOperationSchema = Schema.Struct({
  operationId: ProjectTaskProposalOperationIdSchema,
  kind: Schema.Literal("update"),
  taskId: ProjectTaskIdSchema,
  expectedRevision: ProjectTaskRevisionSchema,
  changes: ProjectTaskUpdateFieldsSchema.check(
    Schema.makeFilter(
      (changes) =>
        Object.values(changes).some((value) => value !== undefined) || "must change a field",
    ),
  ),
});

export const ProjectTaskArchiveOperationSchema = Schema.Struct({
  operationId: ProjectTaskProposalOperationIdSchema,
  kind: Schema.Literal("archive"),
  taskId: ProjectTaskIdSchema,
  expectedRevision: ProjectTaskRevisionSchema,
});

export const ProjectTaskProposalOperationSchema = Schema.Union([
  ProjectTaskCreateOperationSchema,
  ProjectTaskUpdateOperationSchema,
  ProjectTaskArchiveOperationSchema,
]);

export function projectTaskProposalOperationUniquenessIssue(
  operations: readonly ProjectTaskProposalOperation[],
): true | string {
  const operationIds = new Set<string>();
  const taskIds = new Set<string>();
  for (const operation of operations) {
    if (operationIds.has(operation.operationId)) return "operation IDs must be unique";
    operationIds.add(operation.operationId);
    if (operation.kind === "create") continue;
    if (taskIds.has(operation.taskId)) return "a proposal may target each existing task only once";
    taskIds.add(operation.taskId);
  }
  return true;
}

export const ProjectTaskProposalOperationsSchema = Schema.Array(
  ProjectTaskProposalOperationSchema,
).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(PROJECT_TASK_PROPOSAL_MAX_OPERATIONS),
  Schema.makeFilter(projectTaskProposalOperationUniquenessIssue),
);

export const ProjectTaskProposalStatusSchema = Schema.Literals([
  "pending",
  "applied",
  "dismissed",
  "expired",
]);

export const ProjectTaskProposalSchema = Schema.Struct({
  proposalId: ProjectTaskProposalIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  threadId: ThreadIdSchema,
  agentRunId: CanonicalIdentifierSchema,
  requestId: CanonicalIdentifierSchema,
  status: ProjectTaskProposalStatusSchema,
  operations: ProjectTaskProposalOperationsSchema,
  createdBy: UserIdSchema,
  createdAt: ProjectTaskTimestampSchema,
  expiresAt: ProjectTaskTimestampSchema,
  resolvedBy: Schema.optionalKey(UserIdSchema),
  resolvedAt: Schema.optionalKey(ProjectTaskTimestampSchema),
  selectedOperationIds: Schema.optionalKey(
    Schema.Array(ProjectTaskProposalOperationIdSchema).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(PROJECT_TASK_PROPOSAL_MAX_OPERATIONS),
      Schema.makeFilter(
        (ids) => new Set(ids).size === ids.length || "selected operation IDs must be unique",
      ),
    ),
  ),
}).check(
  Schema.makeFilter((proposal) => {
    if (proposal.expiresAt <= proposal.createdAt) return "expiresAt must follow createdAt";
    const hasResolution = proposal.resolvedBy !== undefined && proposal.resolvedAt !== undefined;
    if (proposal.status === "pending") {
      return (
        (!hasResolution && proposal.selectedOperationIds === undefined) ||
        "a pending proposal cannot contain resolution fields"
      );
    }
    if (proposal.status === "applied") {
      if (!hasResolution || proposal.selectedOperationIds === undefined) {
        return "an applied proposal requires its resolver, resolution time, and selection";
      }
      const operationIds = new Set(proposal.operations.map((operation) => operation.operationId));
      return (
        proposal.selectedOperationIds.every((operationId) => operationIds.has(operationId)) ||
        "selected operations must belong to the proposal"
      );
    }
    if (proposal.status === "dismissed") {
      return (
        (hasResolution && proposal.selectedOperationIds === undefined) ||
        "a dismissed proposal requires its resolver and no selection"
      );
    }
    return (
      (proposal.resolvedBy === undefined &&
        proposal.resolvedAt !== undefined &&
        proposal.selectedOperationIds === undefined) ||
      "an expired proposal requires only its resolution time"
    );
  }),
);

export const ListProjectTasksToolInputSchema = Schema.Struct({
  scope: Schema.Literals(["project", "thread"]),
  includeDone: Schema.optionalKey(Schema.Boolean),
});

export const ProposeProjectTaskChangesToolInputSchema = Schema.Struct({
  operations: ProjectTaskProposalOperationsSchema,
});
export const ListProjectTasksToolOutputSchema = Schema.Struct({
  tasks: Schema.Array(ProjectTaskSchema).check(Schema.isMaxLength(PROJECT_TASK_ACTIVE_LIMIT)),
});
export const ProposeProjectTaskChangesToolOutputSchema = Schema.Struct({
  proposalId: ProjectTaskProposalIdSchema,
  status: Schema.Literal("pending"),
  operationCount: PositiveSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(PROJECT_TASK_PROPOSAL_MAX_OPERATIONS),
  ),
  expiresAt: ProjectTaskTimestampSchema,
});

export const ListProjectTasksRepositoryRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  threadId: ThreadIdSchema,
  agentRunId: CanonicalIdentifierSchema,
  scope: Schema.Literals(["project", "thread"]),
  includeDone: Schema.Boolean,
});

export const CreateProjectTaskProposalRepositoryRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  threadId: ThreadIdSchema,
  agentRunId: CanonicalIdentifierSchema,
  requestId: CanonicalIdentifierSchema,
  operations: ProjectTaskProposalOperationsSchema,
});

export const ProjectTaskStandardSchema = toPapersFlowValidationSchema(ProjectTaskSchema);
export const ProjectTaskProposalStandardSchema =
  toPapersFlowValidationSchema(ProjectTaskProposalSchema);
export const ListProjectTasksToolInputStandardSchema = toPapersFlowValidationSchema(
  ListProjectTasksToolInputSchema,
);
export const ProposeProjectTaskChangesToolInputStandardSchema = toPapersFlowValidationSchema(
  ProposeProjectTaskChangesToolInputSchema,
);
export const ListProjectTasksToolOutputStandardSchema = toPapersFlowValidationSchema(
  ListProjectTasksToolOutputSchema,
);
export const ProposeProjectTaskChangesToolOutputStandardSchema = toPapersFlowValidationSchema(
  ProposeProjectTaskChangesToolOutputSchema,
);

export const decodeProjectTask = Schema.decodeUnknownSync(
  ProjectTaskSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeProjectTask = Schema.encodeSync(
  ProjectTaskSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeProjectTaskProposal = Schema.decodeUnknownSync(
  ProjectTaskProposalSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeProjectTaskProposal = Schema.encodeSync(
  ProjectTaskProposalSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeListProjectTasksToolInput = Schema.decodeUnknownSync(
  ListProjectTasksToolInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeProposeProjectTaskChangesToolInput = Schema.decodeUnknownSync(
  ProposeProjectTaskChangesToolInputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeListProjectTasksRepositoryRequest = Schema.decodeUnknownSync(
  ListProjectTasksRepositoryRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeListProjectTasksRepositoryRequest = Schema.encodeSync(
  ListProjectTasksRepositoryRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCreateProjectTaskProposalRepositoryRequest = Schema.decodeUnknownSync(
  CreateProjectTaskProposalRepositoryRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCreateProjectTaskProposalRepositoryRequest = Schema.encodeSync(
  CreateProjectTaskProposalRepositoryRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeProjectTaskTitle = Schema.decodeUnknownSync(
  ProjectTaskTitleSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeProjectTaskDetails = Schema.decodeUnknownSync(
  ProjectTaskDetailsSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeProjectTaskList = Schema.decodeUnknownSync(
  Schema.Array(ProjectTaskSchema).check(Schema.isMaxLength(PROJECT_TASK_ACTIVE_LIMIT)),
  PapersFlowBoundaryParseOptions,
);
export const decodeListProjectTasksToolOutput = Schema.decodeUnknownSync(
  ListProjectTasksToolOutputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeListProjectTasksToolOutput = Schema.encodeSync(
  ListProjectTasksToolOutputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeProposeProjectTaskChangesToolOutput = Schema.decodeUnknownSync(
  ProposeProjectTaskChangesToolOutputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeProposeProjectTaskChangesToolOutput = Schema.encodeSync(
  ProposeProjectTaskChangesToolOutputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeProjectTaskPublicError = Schema.decodeUnknownSync(
  ProjectTaskPublicErrorSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeProjectTaskInternalErrorData = Schema.decodeUnknownSync(
  ProjectTaskInternalErrorDataSchema,
  PapersFlowBoundaryParseOptions,
);
export const areProjectTaskProposalOperationsEquivalent = Schema.toEquivalence(
  ProjectTaskProposalOperationsSchema,
);

export type ProjectTaskId = typeof ProjectTaskIdSchema.Type;
export type ProjectTaskIdEncoded = typeof ProjectTaskIdSchema.Encoded;
export type ProjectTaskProposalId = typeof ProjectTaskProposalIdSchema.Type;
export type ProjectTaskProposalIdEncoded = typeof ProjectTaskProposalIdSchema.Encoded;
export type ProjectTaskProposalOperationId = typeof ProjectTaskProposalOperationIdSchema.Type;
export type ProjectTaskProposalOperationIdEncoded =
  typeof ProjectTaskProposalOperationIdSchema.Encoded;
export type ProjectTaskStatus = typeof ProjectTaskStatusSchema.Type;
export type ProjectTaskPublicErrorCode = typeof ProjectTaskPublicErrorCodeSchema.Type;
export type ProjectTaskPublicError = typeof ProjectTaskPublicErrorSchema.Type;
export type ProjectTask = typeof ProjectTaskSchema.Type;
export type ProjectTaskEncoded = typeof ProjectTaskSchema.Encoded;
export type ProjectTaskCreateOperation = typeof ProjectTaskCreateOperationSchema.Type;
export type ProjectTaskUpdateOperation = typeof ProjectTaskUpdateOperationSchema.Type;
export type ProjectTaskArchiveOperation = typeof ProjectTaskArchiveOperationSchema.Type;
export type ProjectTaskProposalOperation = typeof ProjectTaskProposalOperationSchema.Type;
export type ProjectTaskProposalOperationEncoded = typeof ProjectTaskProposalOperationSchema.Encoded;
export type ProjectTaskProposalStatus = typeof ProjectTaskProposalStatusSchema.Type;
export type ProjectTaskProposal = typeof ProjectTaskProposalSchema.Type;
export type ProjectTaskProposalEncoded = typeof ProjectTaskProposalSchema.Encoded;
export type ListProjectTasksToolInput = typeof ListProjectTasksToolInputSchema.Type;
export type ProposeProjectTaskChangesToolInput =
  typeof ProposeProjectTaskChangesToolInputSchema.Type;
export type ListProjectTasksToolOutput = typeof ListProjectTasksToolOutputSchema.Type;
export type ProposeProjectTaskChangesToolOutput =
  typeof ProposeProjectTaskChangesToolOutputSchema.Type;
export type ListProjectTasksRepositoryRequest = typeof ListProjectTasksRepositoryRequestSchema.Type;
export type CreateProjectTaskProposalRepositoryRequest =
  typeof CreateProjectTaskProposalRepositoryRequestSchema.Type;
